/**
 * Page 5 — Backlog & recency.
 *
 * Every figure on this page rides on the worklist grain. When the bundle lacks it the
 * page does not print zeros: everything built on the open pile says so instead.
 *
 * When the worklist IS loaded it holds only people with a live, non-system worklist, so
 * every figure below is a FLOOR.
 */
import { useEffect, useMemo, useState } from 'react'
import { useAppData } from '@/data/AppData'
import { useLoadedDataset } from '@/data/DatasetProvider'
import { shoreVendor, type Person } from '@/data/selectors'
import { DataTable, type Column } from '@/components/Table'
import { StatStrip, Stat, NotLoaded } from '@/components/Bits'
import { ExportBar } from '@/components/ExportBar'
import { FloatingTip } from '@/components/Charts'
import type { GrainKey } from '@/data/coverage'
import { dateLong, int, pct } from '@/format'
import { useMediaQuery } from '@/ui/useMediaQuery'
import { define } from '@/measures/definitions'
import { AGE_BUCKETS, ageCount, ageShare, pooledWorklistAge, type AgeBucketKey } from '@/measures/core'
import { CardHeading, PopOut } from '@/components/PopOut'

interface Band {
  key: string
  label: string
  color: string
  get: (p: Person) => number
}

/**
 * The pile by when anyone last worked it — the three buckets, in the order and with the
 * labels every surface uses (AGE_BUCKETS). Her ramp: the most recent work is the dark
 * end, never worked is grey. The three come from one worklist row and add up to the pile
 * exactly.
 */
const AGE_COLOUR: Record<AgeBucketKey, string> = {
  last30: 'var(--ord3)',
  over30: 'var(--ord1)',
  neverWorked: 'var(--surface-3)',
}
const AGE_BANDS: Band[] = AGE_BUCKETS.map(({ key, label }) => ({
  key,
  label,
  color: AGE_COLOUR[key],
  get: (p) => (p.age ? ageCount(p.age, key) : 0),
}))

/**
 * Bundles built before 2026-10-01 carry no last-touch date, only what the owner touched
 * in the snapshot's month. Their chart keeps that coarser, true split.
 */
const MONTH_BANDS: Band[] = [
  {
    key: 'worked',
    label: 'Worked this month',
    color: 'var(--ord3)',
    get: (p) => p.openClaims - Math.min(p.openClaims, p.notWorkedThisMonth),
  },
  {
    key: 'notWorked',
    label: 'Not worked this month',
    color: 'var(--surface-3)',
    get: (p) => Math.min(p.openClaims, p.notWorkedThisMonth),
  },
]

/** A right-aligned column off one age band: the count, and beside it its share of the pile. */
const countColumn = (key: string, header: string, help: string, get: (p: Person) => number) => ({
  key,
  header,
  help: define(help),
  align: 'right' as const,
  sort: get,
  render: (p: Person) => (
    <span className="mono" style={{ fontSize: 'var(--t-table)' }}>
      {int(get(p))}
      <span style={{ color: 'var(--ink-3)', display: 'inline-block', minWidth: 38 }}>
        {p.openClaims === 0 ? '—' : pct(get(p) / p.openClaims, 0)}
      </span>
    </span>
  ),
})

export function Backlog() {
  const { scope, openPerson, coverage } = useAppData()
  const dataset = useLoadedDataset()

  const withOpen = useMemo(
    () => scope.people.filter((p) => p.openClaims > 0),
    [scope.people],
  )

  /*
   * Three layers, tracked separately, because the grain has arrived in pieces: the
   * 2026-09-17 bundle carries openClaims only; bundles to 2026-09-30 add not worked
   * this month; from 2026-10-01 each open claim's last touch gives the age buckets.
   * One flag would either blank a figure that is real or print a zero that is not.
   */
  const recency = coverage.worklistRecency
  const aged = coverage.worklistAge

  const totals = useMemo(
    () => ({
      open: withOpen.reduce((a, p) => a + p.openClaims, 0),
      age: pooledWorklistAge(withOpen.map((p) => p.age)),
    }),
    [withOpen],
  )

  const bands = aged ? AGE_BANDS : MONTH_BANDS
  // The band each view ranks by: the one that says most about neglect.
  const worst = bands[bands.length - 1]!

  const recencyRows = useMemo(
    () =>
      withOpen
        .filter((p) => p.openClaims >= 10)
        .sort((a, b) => worst.get(b) - worst.get(a))
        .slice(0, 40),
    [withOpen, worst],
  )

  // Hover tip on the recency rows; any scroll hides it, as hers does.
  const [tip, setTip] = useState<{ p: Person; x: number; y: number } | null>(null)
  useEffect(() => {
    if (!tip) return
    const hide = () => setTip(null)
    window.addEventListener('scroll', hide, true)
    return () => window.removeEventListener('scroll', hide, true)
  }, [tip])
  // Her bars scale to the largest pile of the ranked band in scope.
  const maxWorst = Math.max(1, ...withOpen.map((p) => worst.get(p)))
  const missingAge: GrainKey = coverage.worklist ? 'worklistAge' : 'worklist'
  // Room for the recency chart beside the seven-column bucket table.
  const roomy = useMediaQuery('(min-width: 1280px)')
  const showRecency = coverage.worklist && (aged || recency) && recencyRows.length > 0
  /** The chart and the table side by side, at one height. */
  const pair = roomy && showRecency

  /** The ranked band as a bar, and its share of the pile as her pill. */
  const worstColumns = (key: string, header: string, help: string): Column<Person>[] => [
    {
      key,
      header,
      help: define(help),
      align: 'right',
      sort: (p) => worst.get(p),
      render: (p) => (
        <span className="minibar mono" style={{ fontSize: 'var(--t-table)' }}>
          <span className="track">
            <span
              className="fill"
              style={{
                width: `${Math.min(100, (worst.get(p) / maxWorst) * 100)}%`,
                background: 'var(--crit)',
              }}
            />
          </span>
          {int(worst.get(p))}
        </span>
      ),
    },
    {
      key: `${key}Pct`,
      header: `${header} %`,
      align: 'right',
      sort: (p) => (p.openClaims === 0 ? null : worst.get(p) / p.openClaims),
      // Her pill: red from 80%, amber from 50%, neutral below.
      render: (p) => {
        if (p.openClaims === 0) return <span style={{ color: 'var(--ink-3)' }}>—</span>
        const v = worst.get(p) / p.openClaims
        const band = v >= 0.8 ? 'crit' : v >= 0.5 ? 'warn' : 'neut'
        return <span className={`pts-chip ${band}`}>{pct(v, 0)}</span>
      },
    },
  ]

  /*
   * Every column past "Open" reads a recency layer. With only the open count loaded,
   * any split would be an assertion the data never made, so those columns are built
   * only when their layer is.
   */
  const columns: Column<Person>[] = [
    {
      key: 'person',
      header: 'Owner',
      sort: (p) => p.employee,
      render: (p) => (
        <>
          <span className="nm">{p.employee}</span>
          <br />
          <span className="sub2">
            {p.jobTitle}
            {shoreVendor(p)}
          </span>
        </>
      ),
    },
    {
      key: 'open',
      header: 'Open',
      align: 'right',
      sort: (p) => p.openClaims,
      render: (p) => <span className="mono" style={{ fontSize: 'var(--t-table)' }}>{int(p.openClaims)}</span>,
    },
    ...(aged
      ? [
          // The three buckets in their one order; never worked last, ranked with a bar.
          ...AGE_BUCKETS.flatMap(({ key, label, def }) =>
            key === 'neverWorked'
              ? worstColumns('never', label, def)
              : [countColumn(key, label, def, (p) => (p.age ? ageCount(p.age, key) : 0))],
          ),
        ]
      : []),
    // Only for a bundle without the buckets: the month split is then the best there is.
    ...(recency && !aged ? worstColumns('notWorked', 'Not worked', 'notWorkedThisMonth') : []),
  ]

  return (
    <>
      <h1 className="page-title">Open claims and how fresh they are</h1>
      {aged && dataset.worklistAsOf && (
        <p className="body-note" style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-3)' }}>
          Open claims on {dateLong(dataset.worklistAsOf)}, by when anyone last touched them.
          {dataset.touchHistoryFrom &&
            ` Touch records begin ${dateLong(dataset.touchHistoryFrom)}; never worked means no touch since.`}
        </p>
      )}

      {!coverage.worklist && (
        <div style={{ marginTop: 14 }}>
          <NotLoaded grain="worklist" />
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {/* The pile, then its three buckets as count and share. */}
        <StatStrip cols={4}>
          <Stat
            label="Open claims"
            value={int(totals.open)}
            note={`across ${int(withOpen.length)} people`}
            hero
            absent={coverage.worklist ? undefined : 'worklist'}
          />
          {/*
            The three buckets and nothing else ("way too many measures", 2026-10-01; the
            14- and 15–30-day ones made one, #893): no overlapping windows, no
            not-worked-this-month beside never worked (MEASURE-RULES §5.2a).
          */}
          {AGE_BUCKETS.map(({ key, label, def }) => {
            const share = ageShare(totals.age, key)
            return (
              <Stat
                key={key}
                label={label}
                value={int(ageCount(totals.age, key))}
                note={share == null ? undefined : `${pct(share)} of the open pile`}
                absent={aged ? undefined : missingAge}
                help={define(def)}
              />
            )
          })}
        </StatStrip>
      </div>

      {/*
        Chart and table side by side, as Shayna asked in the 2026-10-02 review (#22).
        With the age buckets the table is seven columns wide, so it takes the larger
        share and the pair only splits on a wide window; below that they stack rather
        than make the table scroll inside half the page.

        Side by side, the two are one height (review #108, 2026-10-06: "Lets make these
        charts even."): the table fills the card beside the chart and scrolls inside
        it, instead of stopping at its own height. See .even-pair.
      */}
      <div
        className={pair ? 'even-pair' : undefined}
        style={{
          display: 'grid',
          gridTemplateColumns: pair
            ? aged
              ? 'minmax(0, 3fr) minmax(0, 5fr)'
              : 'minmax(0, 1fr) minmax(0, 1fr)'
            : aged
              ? 'minmax(0, 1fr)'
              : 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
          alignItems: pair ? 'stretch' : 'start',
          gap: 16,
          marginTop: 16,
        }}
      >
        {showRecency && (
          <PopOut title="Recency of the open pile">
            {({ button }) => (
              <div className="card">
                <CardHeading button={button}>Recency of the open pile</CardHeading>
                <div>
                  {recencyRows.map((p) => (
                    <div
                      key={p.employee}
                      onClick={() => openPerson(p.employee)}
                      onMouseMove={(e) => setTip({ p, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setTip(null)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(90px, 130px) 1fr 44px',
                        alignItems: 'center',
                        gap: 8,
                        height: 17,
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        className="mono"
                        style={{
                          fontSize: 10,
                          color: 'var(--ink-2)',
                          textAlign: 'right',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p.employee}
                      </span>
                      <span style={{ display: 'flex', gap: 1, height: 10 }}>
                        {bands.map((b) => {
                          const share = p.openClaims === 0 ? 0 : b.get(p) / p.openClaims
                          return share <= 0 ? null : (
                            <span
                              key={b.key}
                              style={{
                                width: `${share * 100}%`,
                                minWidth: 0.5,
                                background: b.color,
                                borderRadius: 1.5,
                              }}
                            />
                          )
                        })}
                      </span>
                      <span
                        className="mono"
                        style={{ fontSize: 10, color: 'var(--ink)', textAlign: 'right' }}
                      >
                        {int(p.openClaims)}
                      </span>
                    </div>
                  ))}
                  <div
                    className="mono"
                    style={{ fontSize: 10, color: 'var(--ink-2)', textAlign: 'right', marginTop: 4 }}
                  >
                    open claims
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '5px 14px', flexWrap: 'wrap', margin: '10px 0 2px' }}>
                  {bands.map((b) => (
                    <span key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          width: 11,
                          height: 11,
                          borderRadius: 2,
                          background: b.color,
                          border: '1px solid var(--rule-2)',
                        }}
                      />
                      <span style={{ fontSize: 'var(--t-rank)', color: 'var(--ink-2)' }}>
                        {b.label}
                      </span>
                    </span>
                  ))}
                </div>
                {tip && (
                  <FloatingTip x={tip.x} y={tip.y}>
                    <div className="chart-tip-title">{tip.p.employee}</div>
                    <div style={{ color: 'var(--ink-3)', fontSize: 11, margin: '-3px 0 5px' }}>
                      {tip.p.jobTitle}
                    </div>
                    <div className="chart-tip-row">
                      <span>Open claims</span>
                      <span>{int(tip.p.openClaims)}</span>
                    </div>
                    {bands.map((b) => (
                      <div key={b.key} className="chart-tip-row">
                        <span>{b.label}</span>
                        <span>
                          {int(b.get(tip.p))} (
                          {pct(tip.p.openClaims ? b.get(tip.p) / tip.p.openClaims : 0, 0)})
                        </span>
                      </div>
                    ))}
                  </FloatingTip>
                )}
              </div>
            )}
          </PopOut>
        )}

        <PopOut
          title={aged ? 'Most never worked' : recency ? 'Most not worked this month' : 'Largest open piles'}
        >
          {({ button }) => (
            <div className="card popout-fill">
              <CardHeading button={button}>
                {aged ? 'Most never worked' : recency ? 'Most not worked this month' : 'Largest open piles'}
              </CardHeading>
              {coverage.worklist ? (
                <>
                  {!aged && (
                    <div style={{ margin: '12px 0' }}>
                      <NotLoaded grain={recency ? 'worklistAge' : 'worklistRecency'} />
                    </div>
                  )}
                  <ExportBar
                    filename={
                      aged
                        ? 'open-claims-by-last-worked.csv'
                        : recency
                          ? 'not-worked-this-month.csv'
                          : 'largest-open-piles.csv'
                    }
                    rows={withOpen}
                    columns={[
                      { header: 'Owner', value: (p) => p.employee },
                      { header: 'Job title', value: (p) => p.jobTitle },
                      { header: 'Open', value: (p) => p.openClaims },
                      ...(aged
                        ? AGE_BUCKETS.flatMap(({ key, label }) => [
                            { header: label, value: (p: Person) => (p.age ? ageCount(p.age, key) : null) },
                            {
                              header: `${label} %`,
                              value: (p: Person) => (p.age ? ageShare(p.age, key) : null),
                            },
                          ])
                        : []),
                      ...(recency && !aged
                        ? [
                            { header: 'Not worked this month', value: (p: Person) => p.notWorkedThisMonth },
                            {
                              header: 'Not worked this month %',
                              value: (p: Person) =>
                                p.openClaims === 0 ? null : p.notWorkedThisMonth / p.openClaims,
                            },
                          ]
                        : []),
                    ]}
                  />
                  <div className="card bl-table emp-dense" style={{ padding: 0, overflow: 'hidden' }}>
                    <DataTable
                      storageKey="backlog.people"
                      rows={withOpen}
                      columns={columns}
                      rowKey={(p) => p.employee}
                      onRowClick={(p) => openPerson(p.employee)}
                      initialSort={aged ? 'never' : recency ? 'notWorked' : 'open'}
                      initialDir="desc"
                      sortParam="sort"
                      maxHeight={pair ? undefined : 560}
                      empty="Nobody in this scope appears in the worklist export."
                    />
                  </div>
                </>
              ) : (
                /* No legend and no empty table: a four-colour key above an empty box invites
                   the reader to look for bars that cannot exist. */
                <div style={{ marginTop: 12 }}>
                  <NotLoaded grain="worklist">
                    <p className="notice-why">
                      Ranking people by what is not worked needs the pile. Nothing on this list
                      can be computed until <span className="mono">FactOpenClaims</span> is in
                      the bundle.
                    </p>
                  </NotLoaded>
                </div>
              )}
            </div>
          )}
        </PopOut>
      </div>
    </>
  )
}
